import React, {ReactNode, useCallback, useEffect} from 'react';
import {ButtonDropdownField, DropdownList} from '@forecast-it/design-system';

interface GenericDropdownProps<T, U extends T> {
	options: T[];
	['data-cy']?: string;
	useTriggerWidth?: boolean;
	autoSelect?: boolean;
	renderOption: (option: T) => ReactNode;
	onSelect: (option: U | undefined) => void;
}

const ConditionalDropdown = <T, U extends T>({
	options,
	useTriggerWidth = false,
	autoSelect = true,
	renderOption,
	onSelect,
	'data-cy': dataCy,
	children,
}: React.PropsWithChildren<GenericDropdownProps<T, U>>): React.ReactElement => {
	const handleSelect = useCallback(
		(option: T) => {
			onSelect(option as U);
		},
		[onSelect]
	);

	useEffect(() => {
		if (autoSelect) {
			if (options.length === 1) {
				onSelect(options[0] as U);
			} else {
				onSelect(undefined);
			}
		}
	}, [autoSelect, onSelect, options]);

	if (options.length === 0) {
		return <div>-</div>;
	} else if (autoSelect && options.length === 1) {
		return <div>{renderOption(options[0])}</div>;
	} else {
		const dropdownItems = options.map((option, index) => (
			<DropdownList.Item key={index} onClick={() => handleSelect(option)}>
				{renderOption(option)}
			</DropdownList.Item>
		));

		return (
			<DropdownList>
				<DropdownList.Trigger>
					<ButtonDropdownField data-cy={dataCy}>{children}</ButtonDropdownField>
				</DropdownList.Trigger>
				<DropdownList.Content offset={5} useTriggerWidth={useTriggerWidth}>
					{dropdownItems}
				</DropdownList.Content>
			</DropdownList>
		);
	}
};

export default ConditionalDropdown;
